/* Renderers for the 8 challenge types */
import React, { useState, useMemo } from "react";

function Timer({ remaining }) {
  const warn = remaining <= 10;
  return (
    <div
      className={`tabular-nums font-mono font-bold text-2xl px-3 py-1 border-2 border-black rounded-xl ${
        warn ? "bg-brand-pink text-white animate-pulse" : "bg-brand-yellow"
      }`}
      data-testid="challenge-timer"
    >
      {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}
    </div>
  );
}

function ChallengeFrame({ challenge, remaining, children }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 justify-between">
        <div className="text-xs font-black uppercase tracking-widest text-black/60">
          {typeLabel(challenge.type)}
        </div>
        <Timer remaining={remaining} />
      </div>
      <h2 className="text-3xl sm:text-4xl font-heading font-black tracking-tighter leading-tight">
        {challenge.title}
      </h2>
      <p className="text-black/70 leading-relaxed">{challenge.context}</p>
      {children}
    </div>
  );
}

function typeLabel(t) {
  return {
    quick_decision: "Rychlá volba",
    multi_step: "Vícekroková situace",
    chat_simulation: "Chat simulace",
    feed_analysis: "Analýza feedu",
    sequence: "Seřaď postup",
    vote: "Hlasování třídy",
    reflection: "Reflexe",
    group_task: "Skupinový úkol",
  }[t] || t;
}

/* 1. Quick decision */
export function QuickDecision({ challenge, remaining, onSubmit, submitted }) {
  const [pick, setPick] = useState(null);
  return (
    <ChallengeFrame challenge={challenge} remaining={remaining}>
      <div className="grid gap-3">
        {challenge.choices.map((c) => {
          const active = pick === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setPick(c.id)}
              disabled={submitted}
              data-testid={`choice-${c.id}`}
              className={`text-left brut-card p-4 font-bold transition-all ${
                active ? "bg-brand-orange text-white" : "hover:-translate-y-[2px]"
              } ${submitted ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      <button
        className="brut-btn bg-black text-white w-full"
        disabled={!pick || submitted}
        onClick={() => onSubmit({ choice_id: pick })}
        data-testid="submit-quick"
      >
        {submitted ? "Odesláno ✓" : "Potvrdit volbu"}
      </button>
    </ChallengeFrame>
  );
}

/* 2. Chat simulation */
export function ChatSimulation({ challenge, remaining, onSubmit, submitted }) {
  const [pick, setPick] = useState(null);
  return (
    <ChallengeFrame challenge={challenge} remaining={remaining}>
      <div className="brut-card p-0 overflow-hidden">
        <div className="bg-black text-white px-4 py-2 font-bold flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-brand-yellow border border-white" />
          {challenge.chat_thread[0]?.from || "Chat"}
        </div>
        <div className="p-4 space-y-2 bg-[#f5f5f0] max-h-80 overflow-y-auto">
          {challenge.chat_thread.map((m, i) => (
            <div key={i} className="max-w-[80%]">
              <div className="bg-white border-2 border-black rounded-2xl rounded-tl-sm px-3 py-2">
                <div className="text-sm">{m.text}</div>
              </div>
              <div className="text-[10px] text-black/40 mt-1 pl-2">{m.time}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="text-sm font-bold">Jak odpovíš?</div>
      <div className="grid gap-2">
        {challenge.choices.map((c) => (
          <button
            key={c.id}
            onClick={() => setPick(c.id)}
            disabled={submitted}
            data-testid={`chat-choice-${c.id}`}
            className={`text-left brut-card p-3 text-sm font-bold ${
              pick === c.id ? "bg-brand-cyan" : ""
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>
      <button
        className="brut-btn bg-black text-white w-full"
        disabled={!pick || submitted}
        onClick={() => onSubmit({ choice_id: pick })}
        data-testid="submit-chat"
      >
        {submitted ? "Odesláno ✓" : "Odeslat odpověď"}
      </button>
    </ChallengeFrame>
  );
}

/* 3. Multi-step */
export function MultiStep({ challenge, remaining, onSubmit, submitted }) {
  const [currentStepId, setCurrentStepId] = useState(challenge.steps[0].id);
  const [path, setPath] = useState({});
  const step = useMemo(
    () => challenge.steps.find((s) => s.id === currentStepId),
    [challenge, currentStepId]
  );

  const pickChoice = (choice) => {
    const newPath = { ...path, [currentStepId]: choice.id };
    setPath(newPath);
    const next = choice.next;
    if (next && challenge.steps.some((s) => s.id === next)) {
      setCurrentStepId(next);
    } else {
      onSubmit({ path: newPath });
    }
  };

  if (!step) return null;
  return (
    <ChallengeFrame challenge={challenge} remaining={remaining}>
      <div className="brut-card p-4 bg-brand-yellow/50">
        <div className="text-xs font-black uppercase tracking-wider mb-2">
          {step.prompt}
        </div>
      </div>
      <div className="grid gap-2">
        {step.choices.map((c) => (
          <button
            key={c.id}
            onClick={() => pickChoice(c)}
            disabled={submitted}
            data-testid={`step-${step.id}-${c.id}`}
            className="text-left brut-card p-3 font-bold hover:-translate-y-[1px]"
          >
            {c.label}
          </button>
        ))}
      </div>
    </ChallengeFrame>
  );
}

/* 4. Feed analysis */
export function FeedAnalysis({ challenge, remaining, onSubmit, submitted }) {
  const [selected, setSelected] = useState([]);
  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <ChallengeFrame challenge={challenge} remaining={remaining}>
      <div className="text-sm font-bold">Označ rizikové příspěvky:</div>
      <div className="grid gap-3">
        {challenge.feed_items.map((p) => {
          const on = selected.includes(p.id);
          return (
            <button
              key={p.id}
              onClick={() => toggle(p.id)}
              disabled={submitted}
              data-testid={`feed-${p.id}`}
              className={`text-left brut-card p-4 transition-all ${
                on ? "bg-brand-pink text-white" : "hover:-translate-y-[1px]"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-full bg-black text-white text-xs flex items-center justify-center font-bold">
                  @
                </div>
                <div className="font-bold text-sm">@{p.author}</div>
                {on && (
                  <span className="ml-auto text-[10px] font-black bg-white text-black px-2 py-0.5 rounded-full border border-black">
                    RIZIKOVÉ
                  </span>
                )}
              </div>
              <div className="text-sm leading-relaxed">{p.text}</div>
            </button>
          );
        })}
      </div>
      <button
        className="brut-btn bg-black text-white w-full"
        disabled={submitted}
        onClick={() => onSubmit({ selected })}
        data-testid="submit-feed"
      >
        {submitted ? "Odesláno ✓" : `Odeslat (${selected.length} označeno)`}
      </button>
    </ChallengeFrame>
  );
}

/* 5. Sequence */
export function SequenceTask({ challenge, remaining, onSubmit, submitted }) {
  const [order, setOrder] = useState(challenge.items.map((i) => i.id));
  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= order.length) return;
    const arr = [...order];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setOrder(arr);
  };
  const label = (id) => challenge.items.find((i) => i.id === id)?.label;
  return (
    <ChallengeFrame challenge={challenge} remaining={remaining}>
      <div className="text-sm font-bold">Seřaď od nejdůležitějšího:</div>
      <div className="grid gap-2">
        {order.map((id, idx) => (
          <div key={id} data-testid={`seq-${id}`} className="brut-card p-3 flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-orange text-white rounded-full border-2 border-black flex items-center justify-center font-black">
              {idx + 1}
            </div>
            <div className="flex-1 font-bold text-sm">{label(id)}</div>
            <button
              onClick={() => move(idx, -1)}
              disabled={idx === 0 || submitted}
              className="w-8 h-8 border-2 border-black rounded-md bg-white font-bold disabled:opacity-30"
              data-testid={`seq-up-${id}`}
            >
              ↑
            </button>
            <button
              onClick={() => move(idx, 1)}
              disabled={idx === order.length - 1 || submitted}
              className="w-8 h-8 border-2 border-black rounded-md bg-white font-bold disabled:opacity-30"
              data-testid={`seq-down-${id}`}
            >
              ↓
            </button>
          </div>
        ))}
      </div>
      <button
        className="brut-btn bg-black text-white w-full"
        disabled={submitted}
        onClick={() => onSubmit({ order })}
        data-testid="submit-sequence"
      >
        {submitted ? "Odesláno ✓" : "Potvrdit pořadí"}
      </button>
    </ChallengeFrame>
  );
}

/* 6. Vote */
export function VoteTask({ challenge, remaining, onSubmit, submitted }) {
  const [pick, setPick] = useState(null);
  return (
    <ChallengeFrame challenge={challenge} remaining={remaining}>
      <div className="grid gap-3 sm:grid-cols-2">
        {challenge.choices.map((c) => (
          <button
            key={c.id}
            onClick={() => setPick(c.id)}
            disabled={submitted}
            data-testid={`vote-${c.id}`}
            className={`brut-card p-6 text-left font-bold text-lg transition-all ${
              pick === c.id ? "bg-brand-cyan" : ""
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>
      <button
        className="brut-btn bg-black text-white w-full"
        disabled={!pick || submitted}
        onClick={() => onSubmit({ choice_id: pick })}
        data-testid="submit-vote"
      >
        {submitted ? "Hlas přijat ✓" : "Odevzdat hlas"}
      </button>
    </ChallengeFrame>
  );
}

/* 7. Reflection */
export function Reflection({ challenge, remaining, onSubmit, submitted }) {
  const [mood, setMood] = useState(null);
  const [text, setText] = useState("");
  return (
    <ChallengeFrame challenge={challenge} remaining={remaining}>
      <div className="text-sm font-bold">{challenge.prompt}</div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={submitted}
        maxLength={300}
        rows={4}
        placeholder="Napiš to svými slovy…"
        className="w-full brut-card p-4 font-body text-base focus:outline-none focus:shadow-brut-lg"
        data-testid="reflection-text"
      />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {challenge.mood_choices.map((m) => (
          <button
            key={m.id}
            onClick={() => setMood(m.id)}
            disabled={submitted}
            data-testid={`mood-${m.id}`}
            className={`brut-card p-3 text-sm font-bold ${
              mood === m.id ? "bg-brand-yellow" : ""
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>
      <button
        className="brut-btn bg-black text-white w-full"
        disabled={submitted || (!text && !mood)}
        onClick={() => onSubmit({ reflection_text: text, mood })}
        data-testid="submit-reflection"
      >
        {submitted ? "Odesláno ✓" : "Odeslat reflexi"}
      </button>
    </ChallengeFrame>
  );
}

export const CHALLENGE_RENDERERS = {
  quick_decision: QuickDecision,
  chat_simulation: ChatSimulation,
  multi_step: MultiStep,
  feed_analysis: FeedAnalysis,
  sequence: SequenceTask,
  vote: VoteTask,
  reflection: Reflection,
  group_task: QuickDecision,
};

export function ChallengeRenderer(props) {
  const Comp = CHALLENGE_RENDERERS[props.challenge?.type] || QuickDecision;
  return <Comp {...props} />;
}

export { typeLabel };
